import { useMemo } from 'react'
import contentTypeUtils from '../../contentTypeUtils.js'
import {
  buildConvertedCapturePatch,
  buildDeliveryReceipt,
  buildPublishChecklist,
  buildSourceWorkMetadata,
  normalizeEntityTruthLinks,
} from '../../studioWorkflowUtils.js'
import {
  apiSavePost,
  apiUpdatePost,
  apiDeletePost,
  apiUpdateIdea,
  apiFetchPublishAttempts,
  apiJson,
  apiSubstackPushDraft,
} from '../studioApi.js'
import { useIntegrationHealth } from '../contexts/IntegrationHealthContext'
import { useStudioState } from '../contexts/StudioStateContext'
import type {
  BannerTone,
  DeletePostResponse,
  Idea,
  IntegrationHealth,
  NotionPushResponse,
  Post,
  PublishAttempt,
  StateSetter,
} from '../types'

const { normalizePost, normalizeIdea, getContentType } = contentTypeUtils

export interface PostHandlersApi {
  savePost: (post: Post) => Promise<Post>
  updatePost: (id: string, updates: Partial<Post>) => Promise<Post>
  deletePost: (id: string) => Promise<DeletePostResponse>
  updateIdea: (id: string, updates: Partial<Idea>) => Promise<Idea>
  fetchPublishAttempts: () => Promise<PublishAttempt[]>
  json: <T = unknown>(path: string, init?: RequestInit) => Promise<T>
  substackPushDraft: (postId: string) => Promise<SubstackPushResponse>
}

// Default implementations of the API surface this hook depends on. Tests
// pass an `api` override to inject mocks without touching React.
const DEFAULT_API: PostHandlersApi = Object.freeze({
  savePost: apiSavePost,
  updatePost: apiUpdatePost,
  deletePost: apiDeletePost,
  updateIdea: apiUpdateIdea,
  fetchPublishAttempts: apiFetchPublishAttempts,
  json: apiJson,
  substackPushDraft: apiSubstackPushDraft,
})

// Shape returned by POST /api/substack/push-draft. Kept local to this file
// because the studio's types/ index hasn't been touched in this wave; if
// the interface stabilizes we'll lift it into src/types/.
export interface SubstackPushResponse {
  substackDraftId: string
  previewUrl: string | null
  action: 'created' | 'updated'
  pushedAt: string
  post?: Post
}

export interface DeliveryReceiptInput {
  deliveredAt?: string
  [key: string]: unknown
}

export interface PostHandlersDeps {
  posts?: Post[]
  ideas?: Idea[]
  setPosts: StateSetter<Post[]>
  setArchivedPosts: StateSetter<Post[]>
  setIdeas: StateSetter<Idea[]>
  setPublishAttempts: StateSetter<PublishAttempt[]>
  integrationHealth?: IntegrationHealth
  onToast?: (message: string) => void
  addBanner?: (id: string, text: string, tone: BannerTone) => void
  api?: PostHandlersApi
}

export interface PostHandlers {
  notionPush: (post: Post) => Promise<Post | undefined>
  notionUpdateStatus: (postId: string, status: string) => void
  substackPush: (post: Post) => Promise<Post | undefined>
  handleSave: (post: Post) => Promise<Post>
  handleDelete: (id: string) => Promise<void>
  handleMarkPublished: (
    id: string,
    receiptInput?: DeliveryReceiptInput
  ) => Promise<Post | undefined>
  handleUpdatePost: (id: string, updates: Partial<Post>) => Promise<Post>
  handleEdit: (id: string, content: string) => Promise<void>
  handleRetrySync: (post: Post) => Promise<void>
  handleDuplicate: (post: Post) => Promise<void>
  handleRestorePost: (id: string) => Promise<void>
}

const noopToast = (_message: string): void => {}
const noopBanner = (_id: string, _text: string, _tone: BannerTone): void => {}

// Pure factory — produces the same handlers as the hook but takes every
// dependency explicitly. Designed for unit testing without React; the
// `usePostHandlers` hook is a thin wrapper that wires the StudioState
// and IntegrationHealth contexts in.
//
// Why a factory and not a class: each handler closes over the current
// state at call time. The hook re-invokes this factory whenever state
// changes, which is the React-idiomatic way to keep closures fresh.
// Identity churn on handler functions doesn't matter — consumers
// (tab components) don't memoize on handler identity.
export function createPostHandlers(
  deps: PostHandlersDeps = {} as PostHandlersDeps
): PostHandlers {
  const {
    posts,
    ideas,
    setPosts,
    setArchivedPosts,
    setIdeas,
    setPublishAttempts,
    integrationHealth,
    onToast = noopToast,
    addBanner = noopBanner,
    api = DEFAULT_API,
  } = deps

  async function notionPush(post: Post): Promise<Post | undefined> {
    const notionIntegration =
      integrationHealth?.integrations?.['notion-post-sync']
    if (integrationHealth?.flags?.enableNotionSync === false) return undefined
    if (!notionIntegration?.enabled || !notionIntegration?.available)
      return undefined

    const normalized = normalizePost(post) as Post
    const type = getContentType(normalized.contentType)
    if (!type.supportsNotionSync) return undefined

    try {
      const d: NotionPushResponse = await api.json('/api/notion/push', {
        method: 'POST',
        body: JSON.stringify({ post: normalized }),
      })
      const syncPost = await api.updatePost(normalized.id, {
        notionPageId: d.notionPageId || normalized.notionPageId || null,
        metadata: {
          ...(normalized.metadata || {}),
          lastSyncAttemptAt: new Date().toISOString(),
          lastSyncStatus: 'success',
          lastSyncErrorCode: null,
          lastSyncErrorMessage: null,
        },
      })
      setPosts((prev) =>
        prev.map((item) =>
          item.id === normalized.id ? (normalizePost(syncPost) as Post) : item
        )
      )
      return syncPost
    } catch (rawError) {
      const error = rawError as Error
      try {
        const failurePost = await api.updatePost(normalized.id, {
          metadata: {
            ...(normalized.metadata || {}),
            lastSyncAttemptAt: new Date().toISOString(),
            lastSyncStatus: 'failure',
            lastSyncErrorCode: 'NOTION_POST_SYNC_FAILED',
            lastSyncErrorMessage: error.message || 'Notion sync failed',
          },
        })
        setPosts((prev) =>
          prev.map((item) =>
            item.id === normalized.id
              ? (normalizePost(failurePost) as Post)
              : item
          )
        )
      } catch {
        // Keep the original sync failure as the user-facing error.
      }
      onToast('Notion sync failed')
      addBanner(
        `sync-fail-${normalized.id}`,
        `Notion sync failed for "${normalized.topic}": ${
          error.message || 'Unknown error'
        }`,
        'warning'
      )
      throw error
    }
  }

  function notionUpdateStatus(postId: string, status: string): void {
    if (integrationHealth?.flags?.enableNotionSync === false) return
    api
      .json('/api/notion/update-status', {
        method: 'POST',
        body: JSON.stringify({ postId, status }),
      })
      .catch(() => {})
  }

  // Substack drafts-only push (P1, 2026-05-27). Operator-triggered;
  // does NOT auto-fire on save like notionPush. The integration-health
  // gate matches the notion-post-sync pattern — the studio renders the
  // button regardless, but the handler short-circuits when the
  // integration isn't enabled / available, so a click on a disabled
  // setup produces a toast instead of a wire call.
  async function substackPush(post: Post): Promise<Post | undefined> {
    const substackIntegration =
      integrationHealth?.integrations?.['substack-publishing']
    if (!substackIntegration?.enabled || !substackIntegration?.available) {
      onToast(
        'Substack integration not configured. Set STUDIO_SUBSTACK_ENABLED + SUBSTACK_PUBLICATION_URL.'
      )
      return undefined
    }
    const normalized = normalizePost(post) as Post
    const type = getContentType(normalized.contentType)
    if (!type.supportsSubstackPush) {
      onToast(`${type.label} cannot be pushed to Substack.`)
      return undefined
    }
    try {
      const d: SubstackPushResponse = await api.substackPushDraft(normalized.id)
      // The route already wrote substack metadata server-side and returned
      // the persisted post. We mirror it into local state so the inspector
      // reflects the new draftId / previewUrl without a round-trip.
      const updatedPost = (d.post as Post) || normalized
      setPosts((prev) =>
        prev.map((item) =>
          item.id === normalized.id
            ? (normalizePost(updatedPost) as Post)
            : item
        )
      )
      onToast(
        d.action === 'created'
          ? 'Substack draft created. Open in Substack to publish.'
          : 'Substack draft updated.'
      )
      return updatedPost
    } catch (rawError) {
      const error = rawError as Error
      onToast(`Substack push failed: ${error.message || 'Unknown error'}`)
      addBanner(
        `substack-fail-${normalized.id}`,
        `Substack push failed for "${normalized.topic}": ${
          error.message || 'Unknown error'
        }`,
        'warning'
      )
      throw error
    }
  }

  async function handleSave(post: Post): Promise<Post> {
    const normalizedInput = normalizePost(post) as Post
    const truthLinks = normalizeEntityTruthLinks(normalizedInput)
    const sourceIdea =
      normalizedInput.createdFromIdeaId &&
      ideas?.find((idea) => idea.id === normalizedInput.createdFromIdeaId)
    const metadata: Record<string, unknown> = {
      ...(normalizedInput.metadata || {}),
    }
    if (sourceIdea && !metadata.sourceWork) {
      metadata.sourceWork = buildSourceWorkMetadata(sourceIdea)
    }
    if (normalizedInput.status === 'scheduled' && !metadata.publishChecklist) {
      metadata.publishChecklist = buildPublishChecklist(
        normalizedInput.contentType
      )
    }

    const savedPost = await api.savePost({
      ...normalizedInput,
      ...truthLinks,
      metadata,
    })
    const normalized = normalizePost(savedPost) as Post
    setPosts((prev) => [
      normalized,
      ...prev.filter((item) => item.id !== normalized.id),
    ])

    if (sourceIdea) {
      const converted = await api.updateIdea(
        sourceIdea.id,
        buildConvertedCapturePatch(sourceIdea, normalized.id)
      )
      const normalizedIdea = normalizeIdea(converted) as Idea
      setIdeas((prev) =>
        prev.map((item) =>
          item.id === normalizedIdea.id ? normalizedIdea : item
        )
      )
    }
    notionPush(normalized).catch(() => {})
    return normalized
  }

  async function handleDelete(id: string): Promise<void> {
    const archived = await api.deletePost(id)
    if (archived?.post) {
      const normalized = normalizePost(archived.post) as Post
      setPosts((prev) => prev.filter((p) => p.id !== id))
      setArchivedPosts((prev) => [
        normalized,
        ...prev.filter((item) => item.id !== id),
      ])
    }
  }

  async function handleMarkPublished(
    id: string,
    receiptInput: DeliveryReceiptInput = {}
  ): Promise<Post | undefined> {
    const existing = posts?.find((p) => p.id === id)
    if (!existing) return undefined
    const deliveryReceipt = buildDeliveryReceipt(existing, receiptInput)
    const updatedPost = await api.updatePost(id, {
      status: 'published',
      publishedAt: deliveryReceipt.deliveredAt,
      metadata: {
        ...(existing.metadata || {}),
        deliveryReceipt,
      },
    })
    const normalized = normalizePost(updatedPost) as Post
    setPosts((prev) => prev.map((p) => (p.id === id ? normalized : p)))
    notionPush(normalized).catch(() => {})
    if (getContentType(normalized.contentType).supportsNotionSync) {
      notionUpdateStatus(id, 'published')
    }
    api
      .fetchPublishAttempts()
      .then(setPublishAttempts)
      .catch(() => {})
    return normalized
  }

  async function handleUpdatePost(
    id: string,
    updates: Partial<Post>
  ): Promise<Post> {
    const updatedPost = await api.updatePost(id, updates)
    const normalized = normalizePost(updatedPost) as Post
    setPosts((prev) => prev.map((p) => (p.id === id ? normalized : p)))
    return normalized
  }

  async function handleEdit(id: string, content: string): Promise<void> {
    const updatedPost = await api.updatePost(id, { content })
    const normalized = normalizePost(updatedPost) as Post
    setPosts((prev) => prev.map((p) => (p.id === id ? normalized : p)))
    notionPush(normalized).catch(() => {})
  }

  async function handleRetrySync(post: Post): Promise<void> {
    await notionPush(post)
    onToast('Notion sync retried')
  }

  async function handleDuplicate(post: Post): Promise<void> {
    const normalized = normalizePost(post) as Post
    const dup: Post = {
      ...normalized,
      id: Date.now().toString(),
      status: 'draft',
      scheduledFor: null,
      createdAt: new Date().toISOString(),
    }
    const saved = await api.savePost(dup)
    setPosts((prev) => [normalizePost(saved) as Post, ...prev])
    onToast('Duplicated to drafts')
  }

  async function handleRestorePost(id: string): Promise<void> {
    const restored = await api.updatePost(id, { archivedAt: null })
    const normalized = normalizePost(restored) as Post
    setArchivedPosts((prev) => prev.filter((item) => item.id !== id))
    setPosts((prev) => [normalized, ...prev.filter((item) => item.id !== id)])
  }

  return {
    notionPush,
    notionUpdateStatus,
    substackPush,
    handleSave,
    handleDelete,
    handleMarkPublished,
    handleUpdatePost,
    handleEdit,
    handleRetrySync,
    handleDuplicate,
    handleRestorePost,
  }
}

export interface UsePostHandlersOptions {
  onToast?: (message: string) => void
  addBanner?: (id: string, text: string, tone: BannerTone) => void
}

// React hook — wires the StudioState and IntegrationHealth contexts into
// the pure factory. App.jsx (and any future consumer) uses this rather
// than passing setters around manually.
export function usePostHandlers(
  options: UsePostHandlersOptions = {}
): PostHandlers {
  const { onToast, addBanner } = options
  const studioState = useStudioState()
  const { health } = useIntegrationHealth()

  const {
    posts,
    ideas,
    setPosts,
    setArchivedPosts,
    setIdeas,
    setPublishAttempts,
  } = studioState

  return useMemo(
    () =>
      createPostHandlers({
        posts,
        ideas,
        setPosts,
        setArchivedPosts,
        setIdeas,
        setPublishAttempts,
        integrationHealth: health,
        onToast,
        addBanner,
      }),
    [
      posts,
      ideas,
      setPosts,
      setArchivedPosts,
      setIdeas,
      setPublishAttempts,
      health,
      onToast,
      addBanner,
    ]
  )
}

export const __test__ = { DEFAULT_API }
